import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { actions as appActions } from 'src/features/state'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { actions } from '../../features/shed-management/ShedManagementState'
import { actions as actionsStockManagement } from '../../features/stock-management/StockManagementState'
import { handleOnKeyDown } from 'src/utils/Form'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'src/utils/store'
import { selectFulfillmentCenters } from 'src/features/selector'
import * as S from './FormSearchProducts.styles'
import { FlvRuleDayPeriodEnum } from 'src/features/stock-management/StockManagementTypes'

interface FormData {
  fulfillmentCenterId?: number
  dayPeriod: number
  productsCodes?: string
  productDescription?: string
}

interface FormSearchProductsProps {
  searchWithFC?: boolean
  hasNoneOption?: boolean
  searchFlvProducts?: boolean
  hasRequiredField?: boolean
}

const FormSearchProducts: React.FC<FormSearchProductsProps> = ({
  searchWithFC,
  hasNoneOption = false,
  searchFlvProducts = false,
  hasRequiredField = true,
}) => {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormData>()

  const fulfillmentCenters = useSelector(selectFulfillmentCenters)

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(appActions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  }, [dispatch])

  const onSubmit = (formData: FormData) => {
    const {
      fulfillmentCenterId, dayPeriod, productsCodes, productDescription,
    } = formData

    const formattedProductCodes = productsCodes?.trim().split(/[\r\n]+/).map((code) => code.trim()).filter(Boolean) || []

    if (searchFlvProducts) {
      const selectedDayPeriod = Object.keys(FlvRuleDayPeriodEnum)[dayPeriod].toLowerCase()

      dispatch(actionsStockManagement.getFlvProductInfoForRelease({
        fulfillmentCenterId,
        selectedDayPeriod,
        productsCodes: formattedProductCodes,
        productDescription,
      }))
      return
    }

    dispatch(actions.getProducts({ productsCodes: formattedProductCodes, productDescription }))
  }

  const fulfillmentCenterOptions = useMemo(() => {
    const fcsOptions = fulfillmentCenters.map((fulfillmentCenter) => ({
      id: fulfillmentCenter.id,
      description: fulfillmentCenter.name,
    }))

    if (hasNoneOption) fcsOptions.unshift({ id: 0, description: 'Todos' })
    return fcsOptions
  }, [fulfillmentCenters, hasNoneOption])

  const dayPeriodOptions = useMemo(() => (Object.entries(FlvRuleDayPeriodEnum).map(([, value], index) => ({
    id: index,
    description: value,
  }))), [])

  return (
    <S.ContainerForm onSubmit={handleSubmit(onSubmit)} >
      <S.ContainerSelectFilters>
        {searchWithFC && (
          <S.CustomSelect
            name='fulfillmentCenterId'
            inputProps={{
              ...register(
                'fulfillmentCenterId',
                { required: hasRequiredField && 'Campo obrigatório' },
              ),
              'data-testid': 'fc-search-location',
            }}
            autoComplete='off'
            id='fulfillmentCenterId'
            label='Selecione um FC'
            variant='outlined'
            options={fulfillmentCenterOptions}
            error={!!errors.fulfillmentCenterId}
            helperText={errors.fulfillmentCenterId?.message}
            optionNone={false}
            type='text'
            fullWidth={false}
            autoFocus
            maxHeight='600px'
            dataTestId='fc-search-location'
          />
        )}
        {searchFlvProducts && (
          <S.CustomSelect
            name='dayPeriod'
            inputProps={{
              ...register(
                'dayPeriod',
                { required: 'Campo obrigatório' },
              ),
              'data-testid': 'day-period-select-test-id',
            }}
            autoComplete='off'
            id='dayPeriod'
            label='Selecione a regra do período do dia'
            variant='outlined'
            options={dayPeriodOptions}
            error={!!errors.dayPeriod}
            helperText={errors.dayPeriod?.message}
            optionNone={false}
            type='text'
            fullWidth={false}
            maxHeight='600px'
            dataTestId='day-period-select-test-id'
          />
        )}
      </S.ContainerSelectFilters>
      <S.ContainerTextArea>
        <S.CustomTextArea
          inputProps={{
            ...register('productsCodes', {
              validate: (value) => {
                if (!value && !getValues('productDescription') && hasRequiredField) {
                  return 'Preencha ao menos um campo.'
                }
                return true
              },
            }),
            'data-testid': 'add-skus-input-test',
          }}
          id='productsCodes'
          name='productsCodes'
          error={!!errors.productsCodes}
          label='type.paste.product.codes'
          helperText={errors.productsCodes?.message}
          type='text'
          autoComplete='off'
          fullWidth
          multiline
          rows={2}
          onKeyDown={(e) => handleOnKeyDown(e, 'productDescription')}
        />
        <S.CustomTextArea
          id='productDescription'
          name='productDescription'
          type='text'
          autoComplete='off'
          inputProps={{
            ...register('productDescription', {
              validate: (value) => {
                if (!value && !getValues('productsCodes') && hasRequiredField) {
                  return 'Preencha ao menos um campo.'
                }
                return true
              },
            }),
            'data-testid': 'product-description-test-id',
            maxLength: 150,
          }}
          error={!!errors.productDescription}
          label='product.description'
          helperText={errors.productDescription?.message}
          fullWidth
          multiline
          rows={2}
        />
        <S.CustomButton
          title='Buscar'
          type='submit'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          data-testid='search-product-button'
        />
      </S.ContainerTextArea>
    </S.ContainerForm>
  )
}

export default FormSearchProducts
